import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => undefined,
});

const LIGHT_COLOR = "#ffffff";
const DARK_COLOR = "#0b1220";

function applyThemeClass(effectiveDark: boolean) {
  const root = document.documentElement;
  if (effectiveDark) root.classList.add("dark");
  else root.classList.remove("dark");

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", effectiveDark ? DARK_COLOR : LIGHT_COLOR);
}

/** True when this SPA is running inside an iframe (the EigenAdvisor /team live preview always is). */
export function isEmbedded(): boolean {
  try {
    if (window.self !== window.top) return true; // cross-origin preview iframe
    if (window.location.hash.startsWith("#/embed")) return true; // embed waiting route
    if (new URLSearchParams(window.location.search).has("theme")) return true; // parent passed a theme
  } catch {
    /* cross-origin window.top access can throw -> treat as embedded */
    return true;
  }
  return false;
}

/**
 * When this SPA is embedded (the EigenAdvisor /team live preview), the parent passes its OWN resolved
 * theme as a `?theme=light|dark|system` search param so the embed MATCHES the surrounding advisor UI.
 * Read before the hash (location.search) so there is no flash of the wrong theme on first paint.
 *
 * LIGHT IS THE DEFAULT for an embedded preview: if we are inside an iframe and the parent did NOT
 * explicitly ask for dark, we render LIGHT (the Eigenify brand default) instead of silently following
 * the device's prefers-color-scheme. This holds even on the brief `app.url` (no-param) load and the
 * mint-fallback path, because the iframe check does not depend on the param being present.
 */
function readEmbedTheme(): ThemeMode | null {
  let explicit: ThemeMode | null = null;
  try {
    const p = new URLSearchParams(window.location.search).get("theme");
    if (p === "light" || p === "dark" || p === "system") explicit = p;
  } catch {
    /* non-browser / locked-down context */
  }
  // Parent explicitly asked for dark (advisor is in dark mode) -> honor it.
  if (explicit === "dark") return "dark";
  // Parent asked for light, OR embedded with no explicit dark -> LIGHT is the default for previews.
  if (explicit === "light" || isEmbedded()) return "light";
  return explicit; // "system" passed explicitly, or null (standalone -> fall through to localStorage)
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(
    () => readEmbedTheme() || (localStorage.getItem("theme") as ThemeMode) || "system"
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => {
      const effectiveDark = theme === "system" ? media.matches : theme === "dark";
      applyThemeClass(effectiveDark);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    localStorage.setItem("theme", mode);
    setThemeState(mode);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}








